import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Gift, Lock, Tag } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { ItemList } from "@/components/wizard/StepProducts";
import { StepNav } from "@/components/wizard/Stepper";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 border-b border-dashed border-[#E6DEC6] py-3 text-lg">
    <span className="text-[#5C4A3E]">{k}</span>
    <span className="text-right font-semibold text-[#2C1E16]" data-testid={testid}>{v}</span>
  </div>
);

export const StepPay = ({ order, back }) => {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  const [agree, setAgree] = useState(!!order.consents?.terms);
  const navigate = useNavigate();
  const [freePass, setFreePass] = useState(false);
  useEffect(() => { api.get("/config").then(({ data }) => setMode(data.app_mode)); }, []);
  useEffect(() => { api.get("/me/free-pass").then(({ data }) => setFreePass(data.free)).catch(() => {}); }, []);
  const test = mode === "test";
  const freeTest = test && !(order.items || []).length;
  const gift = freePass && !freeTest;
  const s = order.shipping || {};
  const pay = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.order_id}`, { consent_terms: true });
      const { data } = await api.post(`/orders/${order.order_id}/checkout`, { origin_url: window.location.origin });
      if (data.free_order) {
        toast.success("Your free order is placed!");
        navigate(data.redirect);
      } else if (data.test_mode) {
        toast.success("Test order sent! We're posting your video now.");
        navigate(data.redirect);
      } else window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div>
      <h2 className="text-3xl font-bold text-[#4A2A18]">Review and pay</h2>
      <div className="mt-8"><ItemList order={order} /></div>
      <div className="mt-6">
        <Row k="Keepsake" v={order.title} testid="review-title" />
        <Row k="Ship to" v={`${s.name}, ${s.line1}, ${s.city}, ${s.state} ${s.zip}`} testid="review-address" />
        {order.referral_code && <Row k="Referral code" v={order.referral_code} testid="review-referral" />}
        <Row k="Standard shipping" v={<b className="text-[#2E6930]" data-testid="review-shipping-free">FREE ($0.00)</b>} />
        <div className="flex justify-between py-4 text-2xl font-bold"><span>Total</span><span data-testid="review-total">{gift ? <><s className="mr-2 text-lg font-normal text-[#8A7A6E]">{money(order.total)}</s>$0.00</> : money(order.total)}</span></div>
      </div>
      {gift ? (
        <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-[#2E6930] bg-[#EEF6EC] p-4 text-lg text-[#2C1E16]" data-testid="free-order-banner">
          <Gift className="mt-1 shrink-0 text-[#2E6930]" size={24} />
          <span><b>This order is on us!</b> You've been gifted one free order. No payment is needed: we'll make and ship your keepsake at no cost.</span>
        </div>
      ) : freeTest ? (
        <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-[#D97706] bg-[#FFF7E6] p-4 text-lg text-[#4A3B32]" data-testid="test-mode-banner">
          <FlaskConical className="mt-1 shrink-0 text-[#D97706]" size={24} />
          <span><b>Test mode: you won't be charged.</b> When you continue, we post your video to our YouTube channel and email you a picture of your QR sticker. Nothing is printed or shipped.</span>
        </div>
      ) : (
        <>
          {test && (
            <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-[#D97706] bg-[#FFF7E6] p-4 text-lg text-[#4A3B32]" data-testid="test-card-banner">
              <FlaskConical className="mt-1 shrink-0 text-[#D97706]" size={24} />
              <span><b>Test mode: no real money moves.</b> On Stripe's page, pay with card <b>4242 4242 4242 4242</b>, any future date and any 3-digit code.</span>
            </div>
          )}
          <p className="mt-4 flex items-center gap-2 text-base text-[#5C4A3E]" data-testid="discount-code-hint"><Tag size={18} />
            {order.referral_code ? "Discount codes can't be combined with a referral code." : "Have a discount code? Tap \"Add promotion code\" on Stripe's page."}
          </p>
          <p className="mt-2 flex items-center gap-2 text-base text-[#5C4A3E]"><Lock size={18} /> You'll pay on Stripe's secure page, then come right back here.</p>
        </>
      )}
      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border-2 border-[#E6DEC6] bg-white p-4 text-lg text-[#2C1E16]" data-testid="terms-consent-label">
        <input type="checkbox" className="mt-1 h-6 w-6 shrink-0 accent-[#A83232]" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="terms-consent-checkbox" />
        <span>I agree to the <Link to="/terms" target="_blank" className="font-semibold text-[#A83232] underline" data-testid="pay-terms-link">Terms & Conditions</Link> and <Link to="/privacy" target="_blank" className="font-semibold text-[#A83232] underline" data-testid="pay-privacy-link">Privacy Policy</Link>, including the refund policy for custom items.</span>
      </label>
      <StepNav back={back} next={pay} busy={busy} disabled={!mode || !agree} nextLabel={gift ? "Place my free order" : freeTest ? "Post my video & email my sticker (free test)" : `Pay ${money(order.total)} securely`} testid="pay-button" />
    </div>
  );
};
